/**
 * Zustand store for real-time price data.
 * 
 * Platform-agnostic - works in both web and React Native.
 * 
 * Industry-standard pattern used by Binance, HyperLiquid, Bybit:
 * - Prices stored in Zustand (NOT React state)
 * - Components subscribe to INDIVIDUAL prices via selectors
 * - Only the specific cell that needs a price re-renders when it changes
 * - Parent components NEVER re-render due to price updates
 * 
 * Usage in a table cell:
 * ```tsx
 * const CurrentPriceCell = memo(({ symbol }: { symbol: string }) => {
 *   const price = usePriceStore((state) => state.prices[symbol] ?? 0);
 *   return <div>${formatCurrency(price, 2)}</div>;
 * });
 * ```
 * 
 * This ensures:
 * - Headers never re-render (they don't subscribe to prices)
 * - Action buttons never re-render (they don't subscribe to prices)
 * - Only price cells re-render, and only when THEIR specific price changes
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

interface PriceState {
  // Market prices (Binance Perpetuals) - used for trading
  marketPrices: Record<string, number>;
  
  // Oracle prices (Pyth) - used for funding & liquidations
  oraclePrices: Record<string, number>;
  
  // Last update timestamp per symbol
  timestamps: Record<string, number>;
  
  // Actions
  setPrice: (symbol: string, price: number) => void;
  setPrices: (updates: Array<{ symbol: string; price: number }>) => void;
  setOraclePrices: (updates: Array<{ symbol: string; price: number }>) => void;
  setPricePair: (symbol: string, market: number, oracle: number) => void;
  getPrice: (symbol: string) => number;
  getMarketPrice: (symbol: string) => number;
  getOraclePrice: (symbol: string) => number;
}

export const usePriceStore = create<PriceState>()(
  subscribeWithSelector((set, get) => ({
    marketPrices: {},
    oraclePrices: {},
    timestamps: {},

    /**
     * Set a single price (backwards compatible - sets market price).
     * Only updates if the price actually changed (prevents unnecessary re-renders).
     */
    setPrice: (symbol: string, price: number) => {
      const currentPrice = get().marketPrices[symbol];
      // Only update if price changed (referential equality optimization)
      if (currentPrice !== price) {
        set((state) => ({
          marketPrices: { ...state.marketPrices, [symbol]: price },
          timestamps: { ...state.timestamps, [symbol]: Date.now() },
        }));
      }
    },

    /**
     * Set multiple prices at once (batch update) - backwards compatible.
     * Only updates prices that actually changed.
     */
    setPrices: (updates: Array<{ symbol: string; price: number }>) => {
      const currentPrices = get().marketPrices;
      const newPrices: Record<string, number> = {};
      const newTimestamps: Record<string, number> = {};
      let hasChanges = false;

      for (const { symbol, price } of updates) {
        if (currentPrices[symbol] !== price) {
          newPrices[symbol] = price;
          newTimestamps[symbol] = Date.now();
          hasChanges = true;
        }
      }

      // Only trigger update if at least one price changed
      if (hasChanges) {
        set((state) => ({
          marketPrices: { ...state.marketPrices, ...newPrices },
          timestamps: { ...state.timestamps, ...newTimestamps },
        }));
      }
    },

    /**
     * Set multiple oracle prices at once (batch update).
     * Only updates prices that actually changed.
     */
    setOraclePrices: (updates: Array<{ symbol: string; price: number }>) => {
      const currentPrices = get().oraclePrices;
      const newPrices: Record<string, number> = {};
      const newTimestamps: Record<string, number> = {};
      let hasChanges = false;

      for (const { symbol, price } of updates) {
        if (currentPrices[symbol] !== price) {
          newPrices[symbol] = price;
          newTimestamps[symbol] = Date.now();
          hasChanges = true;
        }
      }

      // Only trigger update if at least one price changed
      if (hasChanges) {
        set((state) => ({
          oraclePrices: { ...state.oraclePrices, ...newPrices },
          timestamps: { ...state.timestamps, ...newTimestamps },
        }));
      }
    },

    /**
     * Set both price types at once for a symbol.
     */
    setPricePair: (symbol: string, market: number, oracle: number) => {
      const state = get();
      const changed = 
        state.marketPrices[symbol] !== market ||
        state.oraclePrices[symbol] !== oracle;
      
      if (changed) {
        set((state) => ({
          marketPrices: { ...state.marketPrices, [symbol]: market },
          oraclePrices: { ...state.oraclePrices, [symbol]: oracle },
          timestamps: { ...state.timestamps, [symbol]: Date.now() },
        }));
      }
    },

    /**
     * Get a price synchronously (backwards compatible - returns market price).
     */
    getPrice: (symbol: string) => {
      return get().marketPrices[symbol] ?? 0;
    },

    /**
     * Get market price (Binance Perpetuals).
     */
    getMarketPrice: (symbol: string) => {
      return get().marketPrices[symbol] ?? 0;
    },

    /**
     * Get oracle price (Pyth).
     */
    getOraclePrice: (symbol: string) => {
      return get().oraclePrices[symbol] ?? 0;
    },
  }))
);

/**
 * Selector hook for a single price (backwards compatible - returns market price).
 * 
 * This is the key pattern: each cell subscribes to ONE price.
 * When BTC price changes, only cells showing BTC re-render.
 * Cells showing ETH, SOL, etc. are completely unaffected.
 */
export const usePrice = (symbol: string): number => {
  return usePriceStore((state) => state.marketPrices[symbol] ?? 0);
};

/**
 * Selector for market price (Binance Perpetuals - used for trading).
 */
export const useMarketPrice = (symbol: string): number => {
  return usePriceStore((state) => state.marketPrices[symbol] ?? 0);
};

/**
 * Selector for oracle price (Pyth - used for funding & liquidations).
 */
export const useOraclePrice = (symbol: string): number => {
  return usePriceStore((state) => state.oraclePrices[symbol] ?? 0);
};

/**
 * Selector for both prices at once.
 * Uses useShallow (Zustand v5) to prevent infinite re-renders when returning objects.
 */
export const usePricePair = (symbol: string) => {
  return usePriceStore(
    useShallow((state) => ({
      market: state.marketPrices[symbol] ?? 0,
      oracle: state.oraclePrices[symbol] ?? 0,
    }))
  );
};

/**
 * Selector for multiple prices (use sparingly - prefer usePrice for cells).
 * Uses useShallow (Zustand v5) to prevent infinite re-renders when returning objects.
 */
export const usePrices = (symbols: string[]): Record<string, number> => {
  return usePriceStore(
    useShallow((state) => {
      const result: Record<string, number> = {};
      for (const symbol of symbols) {
        result[symbol] = state.marketPrices[symbol] ?? 0;
      }
      return result;
    })
  );
};

